import { type FormEvent, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";

import {
  HotelsRawEventName,
  useEmitCoreHotelsRawEventOnce,
} from "@b2bportal/core-hotels";
import type { GuestsSelection, Suggestion } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  HOTELS_AVAILABILITY_PATH,
  HotelNavigationUtility,
  maybeStartFunnel,
} from "@overrides/utilities";
import {
  HotelsAvailabilityActions,
  HotelsAvailabilitySelectors,
} from "../store";

export const useHotelSearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useI18nContext();

  useEmitCoreHotelsRawEventOnce({ name: HotelsRawEventName.HotelSearchLoaded });

  const dateRange = useSelector(
    HotelsAvailabilitySelectors.getSearchFormDateRange
  );
  const guests = useSelector(HotelsAvailabilitySelectors.getSearchFormGuests);
  const place = useSelector(HotelsAvailabilitySelectors.getSearchFormPlace);
  const roomsCount = useSelector(
    HotelsAvailabilitySelectors.getSearchFormRoomsCount
  );

  const handleDateRangeFromChange = (date: string | undefined) => {
    dispatch(HotelsAvailabilityActions.setNewDateFrom(date));
  };
  const handleDateRangeUntilChange = (date: string | undefined) => {
    dispatch(HotelsAvailabilityActions.setNewDateUntil(date));
  };

  const handlePlaceChange = (place: Suggestion) => {
    dispatch(HotelsAvailabilityActions.setNewPlace(place));
  };
  const handleGuestsChange = (guests: GuestsSelection) => {
    dispatch(HotelsAvailabilityActions.setNewGuests(guests));
  };

  const handleRoomsCountChange = (count: number) => {
    dispatch(HotelsAvailabilityActions.setNewRoomsCount(count));
  };

  const isFormValid = useMemo(() => {
    return dateRange?.from != null && dateRange?.until != null && place != null;
  }, [dateRange, place]);

  const availabilityPath = useMemo(() => {
    if (
      isFormValid &&
      place != null &&
      dateRange?.until != null &&
      dateRange?.from != null
    ) {
      const destinationCode = HotelNavigationUtility.placeLabelAndSublabelToUrl(
        place.label,
        place.subLabel
      );
      const searchParams = HotelNavigationUtility.buildSearchParams({
        dateRange: { from: dateRange.from, until: dateRange.until },
        guests,
        roomsCount,
      });

      return `${HOTELS_AVAILABILITY_PATH}/${destinationCode}?${searchParams.toString()}`;
    } else {
      return null;
    }
  }, [dateRange.from, dateRange.until, guests, isFormValid, place, roomsCount]);

  const handleSearch = useCallback(() => {
    if (availabilityPath != null) {
      // Rotate the funnel ID when the committed search differs materially
      // from the current one.
      maybeStartFunnel(
        "lodging",
        HotelNavigationUtility.getHotelFunnelSearch(
          `${location.pathname}${location.search}`
        ),
        HotelNavigationUtility.getHotelFunnelSearch(availabilityPath)
      );
      navigate(availabilityPath);
    }
  }, [availabilityPath, navigate, location.pathname, location.search]);

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSearch();
  };

  return {
    context: {
      dateRange,
      guests,
      place,
      roomsCount,
      locationLabel: t("core-hotels.search.location.label"),
      searchButtonLabel: t("core-hotels.search.button"),
      isFormValid,
      availabilityPath,
    },
    handlers: {
      handleDateRangeFromChange,
      handleDateRangeUntilChange,
      handlePlaceChange,
      handleGuestsChange,
      handleRoomsCountChange,
      onFormSubmit,
      handleSearch,
    },
  };
};
